import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  VolunteerActivism as VolunteerIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingIcon,
  Refresh as RefreshIcon,
  Event as EventIcon,
  Group as GroupIcon,
  AccessTime as TimeIcon,
  EmojiEvents as AchievementIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { volassisAPI } from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import CheckInOut from '../components/CheckInOut';

function Dashboard() {
  const { currentUser, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [aggregateData, setAggregateData] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [recentRecords, setRecentRecords] = useState([]);
  const [error, setError] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [checkInStatus, setCheckInStatus] = useState(null);

  // For improved dropdown population
  const [orgDropdown, setOrgDropdown] = useState([]);
  const [checkInLoading, setCheckInLoading] = useState(false);

  useEffect(() => {
    if (currentUser?.id) {
      loadDashboardData();
      loadOrgDropdown();
    }
  }, [currentUser]);

  // Load organizations for dropdown using /getAggregateOrganizations
  const loadOrgDropdown = async () => {
    try {
      const response = await volassisAPI.getAggregateOrganizations(true);
      if (response && response.output) {
        setOrgDropdown(response.output);
      }
    } catch (error) {
      console.error('Error loading organization dropdown:', error);
      toast.error('Failed to load organizations for check-in');
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load user data
      const userResponse = await volassisAPI.getUserData(currentUser.id);
      if (userResponse && userResponse.output) {
        setUserData(userResponse.output);
      } else {
        setUserData(null);
        return;
      }

      // Load organizations for dropdown
      const orgResponse = await volassisAPI.getOrganizations();
      if (orgResponse && orgResponse.output) {
        setOrganizations(orgResponse.output);
      }

      // Load recent records
      const recordsResponse = await volassisAPI.getUserRecords(currentUser.id, 0, 'all');
      if (recordsResponse && recordsResponse.output) {
        setRecentRecords(recordsResponse.output.slice(0, 5));
      }

      // Get orgId for current user using /getId
      let orgId = '';
      if (userResponse.output.email) {
        const orgIdResponse = await volassisAPI.getId(userResponse.output.email);
        if (orgIdResponse && orgIdResponse.output && orgIdResponse.output.org_id) {
          orgId = orgIdResponse.output.org_id;
          setSelectedOrgId(orgId);
        }
      }

      // Only call getCheckInDisable if orgId is valid
      if (orgId && typeof orgId === 'string' && orgId !== 'Organization') {
        const statusResponse = await volassisAPI.getCheckInDisable(currentUser.id, orgId);
        if (statusResponse && statusResponse.output !== undefined) {
          setCheckInStatus(statusResponse.output);
        }
      } else {
        setCheckInStatus(null);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get organization object by ID
  const getOrgById = (orgId) => orgDropdown.find((org) => org.org_id === orgId);

  // Improved Check-in/Check-out handler
  const handleCheckInOut = async (orgIdParam) => {
    const orgIdToUse = orgIdParam || selectedOrgId;
    // Validate orgId: must be a non-empty string and not "Organization"
    if (!orgIdToUse || orgIdToUse === 'Organization') {
      toast.error('Please select a valid organization.');
      return;
    }
    setCheckInLoading(true);
    try {
      const response = await volassisAPI.checkInCheckOut(currentUser.id, orgIdToUse);
      if (response && response.output) {
        toast.success('Check-in/Check-out successful!');
        // Send emails to user and organization
        const org = getOrgById(orgIdToUse);
        const orgEmail = org?.email ? atob(org.email) : '';
        const userEmail = userData?.email ? atob(userData.email) : '';
        const orgName = org?.name ? atob(org.name) : 'Organization';
        const userName = userData?.first_name ? atob(userData.first_name) : 'User';
        const action = response.output.status || 'Checked In/Out';
        const dateStr = format(new Date(), 'MMM dd, yyyy HH:mm');
        // Email to user
        await volassisAPI.sendEmail(
          userEmail,
          `Check-In/Out Confirmation`,
          `Hi ${userName},\n\nYou have successfully ${action} for ${orgName} on ${dateStr}.\n\nThank you!`
        );
        // Email to organization
        if (orgEmail) {
          await volassisAPI.sendEmail(
            orgEmail,
            `Volunteer ${action}`,
            `${userName} (${userEmail}) has ${action} for your organization (${orgName}) on ${dateStr}.`
          );
        }
        loadDashboardData(); // Refresh data
      } else {
        toast.error('Check-in/Check-out failed');
      }
    } catch (error) {
      console.error('Check-in/Check-out error:', error);
      toast.error('An error occurred during check-in/check-out');
    } finally {
      setCheckInLoading(false);
    }
  };

  const handleApproveAllHours = async (orgId) => {
    try {
      const response = await volassisAPI.approveAllHours(currentUser.id, orgId);
      if (response && response.output) {
        toast.success('All hours approved successfully!');
        loadDashboardData(); // Refresh data
      } else {
        toast.error('Failed to approve hours');
      }
    } catch (error) {
      console.error('Approve hours error:', error);
      toast.error('An error occurred while approving hours');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'denied':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Welcome Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #236bbf 0%, #9831bc 100%)', color: 'white' }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <VolunteerIcon sx={{ fontSize: 48 }} />
          </Grid>
          <Grid item xs>
            <Typography variant="h4" gutterBottom>
              Welcome back, {userData?.first_name || 'Volunteer'}!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              {isAdmin ? 'Administrator Dashboard' : 'Volunteer Dashboard'}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<RefreshIcon />}
              onClick={loadDashboardData}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Hours
                  </Typography>
                  <Typography variant="h4">
                    {aggregateData?.total_hours || 0}
                  </Typography>
                </Box>
                <TimeIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Approved Hours
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {aggregateData?.approved_hours || 0}
                  </Typography>
                </Box>
                <CheckIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending Hours
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {aggregateData?.pending_hours || 0}
                  </Typography>
                </Box>
                <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Organizations
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {organizations?.length || 0}
                  </Typography>
                </Box>
                <BusinessIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} lg={8}>
          {/* Quick Actions */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                {organizations?.map((org) => (
                  <Grid item xs={12} sm={6} key={org.org_id}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {org.name}
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleCheckInOut(org.org_id)}
                        >
                          Check In/Out
                        </Button>
                        {isAdmin && (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => handleApproveAllHours(org.org_id)}
                          >
                            Approve Hours
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Recent Records */}
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Volunteer Records
              </Typography>
              {recentRecords.length > 0 ? (
                <List>
                  {recentRecords.map((record, index) => (
                    <React.Fragment key={record.id || `${record.organization_name}-${record.date}-${index}`}>
                      <ListItem>
                        <ListItemIcon>
                          <EventIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${record.organization_name || 'Unknown Organization'}`}
                          secondary={
                            (() => {
                              let dateStr = 'Unknown Date';
                              if (record.date) {
                                const parsedDate = new Date(record.date);
                                if (!isNaN(parsedDate.getTime())) {
                                  dateStr = format(parsedDate, 'MMM dd, yyyy');
                                }
                              }
                              return `${dateStr} - ${record.hours} hours`;
                            })()
                          }
                        />
                        <Chip
                          label={record.status || 'Unknown'}
                          color={getStatusColor(record.status)}
                          size="small"
                        />
                      </ListItem>
                      {index < recentRecords.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="textSecondary" align="center" sx={{ py: 2 }}>
                  No recent records found
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} lg={4}>
          {/* Hours Progress */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hours Progress
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Approved</Typography>
                  <Typography variant="body2">
                    {aggregateData?.approved_hours || 0} / {aggregateData?.total_hours || 0}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={aggregateData?.total_hours ? (aggregateData.approved_hours / aggregateData.total_hours) * 100 : 0}
                  color="success"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Pending</Typography>
                  <Typography variant="body2">
                    {aggregateData?.pending_hours || 0} hours
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={aggregateData?.total_hours ? (aggregateData.pending_hours / aggregateData.total_hours) * 100 : 0}
                  color="warning"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* User Profile Summary */}
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Profile Summary
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Name
                </Typography>
                <Typography variant="body1">
                  {userData?.first_name} {userData?.last_name}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {userData?.email}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Role
                </Typography>
                <Chip
                  label={isAdmin ? 'Administrator' : 'Volunteer'}
                  color={isAdmin ? 'primary' : 'secondary'}
                  size="small"
                />
              </Box>
              
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={userData?.active ? 'Active' : 'Inactive'}
                  color={userData?.active ? 'success' : 'error'}
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Improved Check-In/Check-Out UI */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Check-In/Check-Out
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Select Organization:
              </Typography>
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '16px',
                }}
              >
                <option value="">-- Select --</option>
                {orgDropdown.map((org) => (
                  <option key={org.org_id} value={org.org_id}>
                    {org.name ? atob(org.name) : 'Organization'}
                  </option>
                ))}
              </select>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                color="primary"
                disabled={checkInLoading || !selectedOrgId}
                onClick={() => handleCheckInOut()}
                sx={{ minWidth: 150 }}
              >
                {checkInLoading ? <CircularProgress size={24} /> : 'Check In / Out'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            {organizations?.map((org) => (
              <Grid item xs={12} sm={6} key={org.org_id || org.name}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {org.name ? atob(org.name) : org.name}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleCheckInOut(org.org_id)}
                    >
                      Check In/Out
                    </Button>
                    {isAdmin && (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleApproveAllHours(org.org_id)}
                      >
                        Approve Hours
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Recent Records */}
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Volunteer Records
          </Typography>
          {recentRecords.length > 0 ? (
            <List>
              {recentRecords.map((record, index) => (
                <React.Fragment key={record.id || `${record.organization_name}-${record.date}-${index}`}>
                  <ListItem>
                    <ListItemIcon>
                      <EventIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${record.organization_name || 'Unknown Organization'}`}
                      secondary={
                        (() => {
                          let dateStr = 'Unknown Date';
                          if (record.date) {
                            const parsedDate = new Date(record.date);
                            if (!isNaN(parsedDate.getTime())) {
                              dateStr = format(parsedDate, 'MMM dd, yyyy');
                            }
                          }
                          return `${dateStr} - ${record.hours} hours`;
                        })()
                      }
                    />
                    <Chip
                      label={record.status || 'Unknown'}
                      color={getStatusColor(record.status)}
                      size="small"
                    />
                  </ListItem>
                  {index < recentRecords.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography color="textSecondary" align="center" sx={{ py: 2 }}>
              No recent records found
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* CheckInOut Component - Example usage */}
      {(isAdmin) && (
        <CheckInOut
          userId={currentUser.id}
          orgId={selectedOrgId}
          initialStatus={checkInStatus}
          onStatusChange={setCheckInStatus}
        />
      )}
    </Box>
  );
}

export default Dashboard;
